import { useMemo, useState } from "react";
import { differenceInCalendarDays, format, subDays } from "date-fns";
import { fr } from "date-fns/locale";
import {
  AlertTriangle,
  FileSpreadsheet,
  FileText,
  ReceiptText,
  RefreshCw,
} from "lucide-react";
import { Link } from "react-router-dom";
import { cn, displayPrice } from "@/lib/utils";
import toast from "react-hot-toast";
import {
  SalesReportApi,
  useGetSalesReportQuery,
  useLazyGetSalesReportPdfQuery,
  useLazyGetSalesReportCsvQuery,
} from "@/redux/services/pos/salesReport.api";
import { RegisterSessionApi } from "@/redux/services/pos/registerSession.api";
import { SalesReport } from "@/redux/services/pos/pos.type";
import { OrderApi } from "@/redux/services/order/order.api";
import { IngredientApi } from "@/redux/services/inventory/ingredient.api";
import { useAppDispatch } from "@/redux/features/hooks";
import { useRestaurantCurrency } from "@/lib/hooks";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { DateRangePicker } from "@/components/Dashboard/DateRangePicker";
import FinancialOverviewCard from "@/components/expenses/FinancialOverviewCard";
import TotalCollectedSection from "./TotalCollectedSection";
import OrderMixSection from "./OrderMixSection";
import TopSellingItemsSection from "./TopSellingItemsSection";
import AnomaliesSection from "./AnomaliesSection";
import RestockSection from "./RestockSection";
import ReportChartsSection from "./ReportChartsSection";
import SectionBreakdownCard from "./SectionBreakdownCard";
import PeakTimesSection from "./PeakTimesSection";
import NeverSoldSection from "./NeverSoldSection";
import { reportCard } from "./reportCard";

interface SalesReportDashboardProps {
  restaurantId: string;
}

function SalesReportDashboard({ restaurantId }: SalesReportDashboardProps) {
  // Default to a single day (today) so the daily-report behaviour is unchanged;
  // the picker lets managers widen to a week, month, etc.
  const today = format(new Date(), "yyyy-MM-dd");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const currency = useRestaurantCurrency();
  const dispatch = useAppDispatch();
  const { can } = usePermissions();

  const {
    data: report,
    isLoading,
    isFetching,
  } = useGetSalesReportQuery({ restaurantId, startDate, endDate });

  // The immediately-preceding period of equal length, so every headline metric
  // can show how it moved versus a comparable window (the "how am I doing"
  // signal a manager comes here for).
  const { prevStart, prevEnd } = useMemo(() => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const length = differenceInCalendarDays(end, start) + 1;
    const previousEnd = subDays(start, 1);
    const previousStart = subDays(previousEnd, length - 1);
    return {
      prevStart: format(previousStart, "yyyy-MM-dd"),
      prevEnd: format(previousEnd, "yyyy-MM-dd"),
    };
  }, [startDate, endDate]);

  const { data: previousReport } = useGetSalesReportQuery(
    { restaurantId, startDate: prevStart, endDate: prevEnd },
    { skip: !restaurantId }
  );

  const [triggerPdf, { isFetching: isPdfLoading }] =
    useLazyGetSalesReportPdfQuery();
  const [triggerCsv, { isFetching: isCsvLoading }] =
    useLazyGetSalesReportCsvQuery();

  const handleDateChange = (newStartDate: string, newEndDate: string) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleRefresh = () => {
    dispatch(SalesReportApi.util.invalidateTags(["salesReport"]));
    dispatch(RegisterSessionApi.util.invalidateTags(["registerSession"]));
    dispatch(OrderApi.util.invalidateTags(["order-list"]));
    dispatch(IngredientApi.util.invalidateTags(["ingredient"]));
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadPdf = async () => {
    try {
      const result = await triggerPdf({
        restaurantId,
        startDate,
        endDate,
      }).unwrap();
      downloadBlob(result, `sales_report_${startDate}_${endDate}.pdf`);
    } catch {
      toast.error("Erreur lors du téléchargement du PDF");
    }
  };

  const handleDownloadCsv = async () => {
    try {
      const { blob, filename } = await triggerCsv({
        restaurantId,
        startDate,
        endDate,
      }).unwrap();
      downloadBlob(blob, filename ?? `sales_report_${startDate}_${endDate}.csv`);
    } catch {
      toast.error("Erreur lors du téléchargement du CSV");
    }
  };

  const reportLoading = isLoading || isFetching;

  return (
    <div className="mt-6 space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex w-full flex-col gap-1 sm:flex-1">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">
            Période
          </span>
          <DateRangePicker
            className="w-full sm:w-auto"
            startDate={startDate}
            endDate={endDate}
            onDateChange={handleDateChange}
          />
          <span className="text-xs text-muted-foreground">
            {prevStart === prevEnd
              ? `comparé au ${formatDayLabel(prevStart)}`
              : `comparé au ${formatDayLabel(prevStart)} – ${formatDayLabel(prevEnd)}`}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Utility, not a primary action → quiet ghost with an icon that
              spins while refreshing. */}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isFetching}
            aria-label="Actualiser les données"
            className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
          >
            <RefreshCw
              className={cn("h-4 w-4", isFetching && "animate-spin")}
            />
            Actualiser
          </button>
          {/* Standalone payments ledger — the per-transaction "all money that
              moved" view, seeded with the current date range. Deliberately not
              tied to a per-method report figure (the two count differently). */}
          <Button asChild size="sm" variant="outline">
            <Link to={`/reports/payments?start=${startDate}&end=${endDate}`}>
              <ReceiptText className="h-4 w-4 mr-2" />
              Journal des paiements
            </Link>
          </Button>
          {report && (
            <>
              {/* PDF — the richer, formatted export → solid brand accent. */}
              <Button
                onClick={handleDownloadPdf}
                disabled={isPdfLoading}
                isLoading={isPdfLoading}
                size="sm"
              >
                <FileText className="h-4 w-4 mr-2" />
                PDF
              </Button>
              {/* CSV — the raw data export → teal outline, clearly a sibling
                  of PDF but visually distinct. */}
              <Button
                onClick={handleDownloadCsv}
                disabled={isCsvLoading}
                isLoading={isCsvLoading}
                size="sm"
                variant="outline"
                className="border-[#066a69]/40 text-[#066a69] hover:bg-[#066a69]/10 hover:text-[#066a69]"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                CSV
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Revenue-vs-expenses summary for the selected period. Reuses the page
          date range and sits above the sales detail. Needs expenses.view on top
          of the reports.view the whole page already requires. */}
      {can("expenses.view") && (
        <FinancialOverviewCard startDate={startDate} endDate={endDate} />
      )}

      {reportLoading && <ReportSkeleton />}

      {!reportLoading && !report && <EmptyReport />}

      {!reportLoading && report && (
        <div className="space-y-8">
          {/* Santé — the headline numbers, with period-over-period movement. */}
          <TotalCollectedSection
            report={report}
            previousReport={previousReport}
            currency={currency}
            restaurantId={restaurantId}
            startDate={startDate}
            endDate={endDate}
          />

          {/* Tendance — how the period moved over time (full width). */}
          <ReportChartsSection
            restaurantId={restaurantId}
            startDate={startDate}
            endDate={endDate}
          />

          {/* Répartition — the two breakdown cards side by side. */}
          <div className="grid gap-6 lg:grid-cols-2 items-start">
            <OrderMixSection
              ordersByType={report.ordersByType}
              ordersBySource={report.ordersBySource}
              previousOrdersByType={previousReport?.ordersByType}
              previousOrdersBySource={previousReport?.ordersBySource}
            />
            <SectionBreakdownCard
              restaurantId={restaurantId}
              startDate={startDate}
              endDate={endDate}
            />
          </div>

          {/* Ce qui se vend — most and least sold dishes side by side. */}
          <div className="grid gap-6 lg:grid-cols-2">
            <TopSellingItemsSection
              title="Plats les plus vendus"
              order="top"
              storageKey="reports.topSellingItems.sort"
              items={report.topSellingItems ?? []}
              previousItems={previousReport?.topSellingItems}
              currency={currency}
            />
            <TopSellingItemsSection
              title="Plats les moins vendus"
              order="least"
              storageKey="reports.leastSellingItems.sort"
              items={report.leastSellingItems ?? []}
              previousItems={previousReport?.leastSellingItems}
              currency={currency}
            />
          </div>

          {/* Never sold — available items with no sales, to prune the menu.
              Hidden when nothing sold at all (an empty period would list the
              whole menu, which is noise rather than a signal). */}
          {report.paidOrders > 0 && (
            <NeverSoldSection items={report.neverSoldItems ?? []} />
          )}

          {/* Quand — when the restaurant is busiest, to drive staffing. */}
          <ReportGroup title="Fréquentation">
            <PeakTimesSection
              restaurantId={restaurantId}
              startDate={startDate}
              endDate={endDate}
            />
          </ReportGroup>

          {/* À surveiller — everything that needs a manager's action. */}
          <ReportGroup title="À surveiller">
            <OutstandingCallout report={report} currency={currency} />
            <AnomaliesSection
              restaurantId={restaurantId}
              startDate={startDate}
              endDate={endDate}
              currency={currency}
            />
            {/* Reads the ingredients endpoint, which needs inventory.view
                (SFRESTO-136). */}
            {can("inventory.view") && (
              <RestockSection restaurantId={restaurantId} />
            )}
          </ReportGroup>
        </div>
      )}
    </div>
  );
}

function formatDayLabel(isoDate: string): string {
  return format(new Date(isoDate), "d MMM", { locale: fr });
}

function ReportSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-40 rounded-lg bg-muted/60 animate-pulse" />
      <div className="h-48 rounded-lg bg-muted/60 animate-pulse" />
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="h-32 rounded-lg bg-muted/60 animate-pulse" />
        <div className="h-32 rounded-lg bg-muted/60 animate-pulse" />
      </div>
    </div>
  );
}

function EmptyReport() {
  return (
    <div
      className={cn(
        reportCard,
        "flex flex-col items-center justify-center py-16 px-4 text-center",
      )}
    >
      <FileText className="h-12 w-12 text-muted-foreground mb-4" />
      <p className="text-sm text-muted-foreground">
        Aucune activité enregistrée pour cette période.
      </p>
    </div>
  );
}

function ReportGroup({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h2>
      {children}
    </section>
  );
}

function OutstandingCallout({
  report,
  currency,
}: {
  report: SalesReport;
  currency: string;
}) {
  const outstanding = Math.max(
    report.expectedTotalRevenue - report.actualTotalRevenue,
    0
  );
  const unpaidCount = Math.max(report.totalOrders - report.paidOrders, 0);
  if (outstanding <= 0) {
    return null;
  }
  return (
    <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
      <div className="text-sm">
        <p className="font-medium">
          {displayPrice(outstanding, currency)} encore à encaisser
        </p>
        <p className="text-amber-800/80">
          {unpaidCount} commande{unpaidCount > 1 ? "s" : ""} non réglée
          {unpaidCount > 1 ? "s" : ""} sur la période.
        </p>
      </div>
    </div>
  );
}

export default SalesReportDashboard;
